import React from "react";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import Grid from "@mui/material/Grid";
import formatDate from "date-fns/format";
import Stack from "@mui/material/Stack";
import SessionDetailsItem from "@app.automotus.io/components/SessionDetailsItem";
import type { Theme } from "@mui/material/styles";

/**
 * Displays basic details about a transaction for a payee user.
 */
export const SessionDetails: React.FC<SessionDetailsProps> = ({
  loading,
  startTime,
  endTime,
  addressState,
  addressCity,
  addressStreet,
  licensePlateNumber,
  licensePlateStateCode,
}) => (
  <Paper sx={{ p: 3 }}>
    <Stack spacing={3}>
      <Typography variant={"h5"} fontWeight={400} mb={3}>
        Transaction Details
      </Typography>
      <Grid
        container
        rowSpacing={2.5}
        columnSpacing={3}
        ml={(theme: Theme) => `${theme.spacing(-3)} !important`}
        mt={(theme: Theme) => `${theme.spacing(-2.5)} !important`}
      >
        {/* A trip charge (the airport LPR entry capture) is an *instant*: it has no
            session_ended_at, so a start-to-end range is meaningless and an epoch fallback for the
            missing end rendered 12/31/1969. Fall back to startTime for both fields when there is no
            end. endTime stays first in the Date precedence -- for a park the billable moment is the
            session end, and that must not change. */}
        <SessionDetailsItem
          loading={loading}
          title={"Date"}
          content={endTime ? formatDate(endTime, "M/d/yyyy") : startTime && formatDate(startTime, "M/d/yyyy")}
          skeletonWidth={104}
        />
        <SessionDetailsItem
          loading={loading}
          title={"Time"}
          content={
            startTime &&
            (endTime
              ? `${formatDate(startTime, "hh:mm a")} - ${formatDate(endTime, "hh:mm a")}`
              : formatDate(startTime, "hh:mm a"))
          }
          skeletonWidth={166}
        />
        <SessionDetailsItem loading={loading} title={"Address"} content={addressStreet} skeletonWidth={111} />
        <SessionDetailsItem
          loading={loading}
          title={"City"}
          content={addressCity && addressState && `${addressCity}, ${addressState}`}
          skeletonWidth={112}
        />
        <SessionDetailsItem
          loading={loading}
          title={"Vehicle"}
          content={licensePlateNumber && licensePlateStateCode ? `${licensePlateStateCode} ${licensePlateNumber}` : "-"}
          skeletonWidth={104}
        />
      </Grid>
    </Stack>
  </Paper>
);

/** Props passed to initialize a {@link SessionDetails} component */
export interface SessionDetailsProps {
  /** Loading status of component */
  loading?: boolean;
  /**
   * Start time of event. Omitted when the underlying timestamp is absent -- never substitute a
   * placeholder Date, which is how the epoch (12/31/1969) used to reach the screen.
   */
  startTime?: Date;
  /**
   * End time of event. Absent for an instantaneous event such as a trip charge, which has no
   * session end; the Date and Time fields fall back to {@link SessionDetailsProps.startTime}.
   */
  endTime?: Date;
  /** Street address where event occurred. Must be defined if component is loaded */
  addressStreet?: string;
  /** City where event occurred. Must be defined if component is loaded */
  addressCity?: string;
  /** State where event occurred. Must be defined if component is loaded */
  addressState?: string;
  /** License plate number */
  licensePlateNumber?: string;
  /** License plate state code */
  licensePlateStateCode?: string;
}

export default SessionDetails;
